import i18n from '@/i18n';

import {
  ProductTypeName,
  ShippingMethod,
  WarehouseShippingMethod,
  PriceRangeFilterEnum,
  RamRangeFilterEnum,
  SsdRangeFilterEnum,
  SizeRangeFilterEnum,
  CellularGenerationFilterEnum,
  ProductTypePropertySlugEnum,
} from '@electricjs/core_entity-client';

import { formatSlugToDisplayName } from '@common';

import { DynamicFilterKey } from '@/types/storefront';

import getProductTypeLabel from '@/components/Assets/helpers/getProductTypeLabel';

export const getWarehouseShippingMethodDisplayName = (
  method: WarehouseShippingMethod
): string => {
  const warehouseShippingMethodMap: Record<WarehouseShippingMethod, string> = {
    [WarehouseShippingMethod.Ground]: i18n.t(
      'storefront:sections.shipping.warehouseMethod.ground'
    ),
    [WarehouseShippingMethod.Expedited]: i18n.t(
      'storefront:sections.shipping.warehouseMethod.expedited'
    ),
  };

  return warehouseShippingMethodMap[method];
};

export const getCatalogShippingMethodDisplayName = (
  method: ShippingMethod
): string => {
  const catalogShippingMethodMap: Record<ShippingMethod, string> = {
    [ShippingMethod.ElectronicDelivery]: i18n.t(
      'storefront:sections.shipping.method.electronicDelivery'
    ),
    [ShippingMethod.UpsGround]: i18n.t(
      'storefront:sections.shipping.method.upsGround'
    ),
    [ShippingMethod.UpsNextDayAir]: i18n.t(
      'storefront:sections.shipping.method.upsNextDayAir'
    ),
    [ShippingMethod.LocalDelivery]: i18n.t(
      'storefront:sections.shipping.method.localDelivery'
    ),
  };

  return catalogShippingMethodMap[method];
};

export const getDynamicFilterDisplayName = (
  filterKey: DynamicFilterKey
): string => {
  const displayNameMap: Record<DynamicFilterKey, string> = {
    [DynamicFilterKey.PRICE]: i18n.t('storefront:filters.labels.price'),
    [DynamicFilterKey.RAM]: i18n.t('storefront:filters.labels.ram'),
    [DynamicFilterKey.SIZE]: i18n.t('storefront:filters.labels.size'),
    [DynamicFilterKey.MANUFACTURER]: i18n.t('storefront:filters.labels.brand'),
    [DynamicFilterKey.PROCESSOR]: i18n.t('storefront:filters.labels.processor'),
    [DynamicFilterKey.STORAGE]: i18n.t('storefront:filters.labels.storage'),
    [DynamicFilterKey.TYPE]: i18n.t('storefront:filters.labels.type'),
    [DynamicFilterKey.COLOR]: i18n.t('storefront:filters.labels.color'),
    [DynamicFilterKey.CONNECTIVITY]: i18n.t(
      'storefront:filters.labels.connectivity'
    ),
    [DynamicFilterKey.CARRIER]: i18n.t('storefront:filters.labels.carrier'),
    [DynamicFilterKey.TELEPHONE_TYPE]: i18n.t(
      'storefront:filters.labels.telephoneType'
    ),
    [DynamicFilterKey.LINES_SUPPORTED]: i18n.t(
      'storefront:filters.labels.linesSupported'
    ),
    [DynamicFilterKey.POWER_OVER_ETHERNET]: i18n.t(
      'storefront:filters.labels.powerOverEthernet'
    ),
    [DynamicFilterKey.KEYBOARD_LANGUAGE]: i18n.t(
      'storefront:filters.labels.keyboardLanguage'
    ),
  };

  return displayNameMap[filterKey];
};

const getPriceFilterOptionDisplayName = (
  enumValue: PriceRangeFilterEnum
): string => {
  const displayNameMap: Record<PriceRangeFilterEnum, string> = {
    [PriceRangeFilterEnum._3999AndLess]: i18n.t(
      'storefront:filters.options.price._3999AndLess'
    ),
    [PriceRangeFilterEnum._40AndUp]: i18n.t(
      'storefront:filters.options.price._40AndUp'
    ),
    [PriceRangeFilterEnum._4999AndLess]: i18n.t(
      'storefront:filters.options.price._4999AndLess'
    ),
    [PriceRangeFilterEnum._50AndUp]: i18n.t(
      'storefront:filters.options.price._50AndUp'
    ),
    [PriceRangeFilterEnum._19999AndLess]: i18n.t(
      'storefront:filters.options.price._19999AndLess'
    ),
    [PriceRangeFilterEnum._200AndUp]: i18n.t(
      'storefront:filters.options.price._200AndUp'
    ),
    [PriceRangeFilterEnum._49999AndLess]: i18n.t(
      'storefront:filters.options.price._49999AndLess'
    ),
    [PriceRangeFilterEnum._50099999]: i18n.t(
      'storefront:filters.options.price._50099999'
    ),
    [PriceRangeFilterEnum._99999AndLess]: i18n.t(
      'storefront:filters.options.price._99999AndLess'
    ),
    [PriceRangeFilterEnum._1000199999]: i18n.t(
      'storefront:filters.options.price._1000199999'
    ),
    [PriceRangeFilterEnum._1000AndUp]: i18n.t(
      'storefront:filters.options.price._1000AndUp'
    ),
    [PriceRangeFilterEnum._2000299999]: i18n.t(
      'storefront:filters.options.price._2000299999'
    ),
    [PriceRangeFilterEnum._3000AndUp]: i18n.t(
      'storefront:filters.options.price._3000AndUp'
    ),
  };
  return displayNameMap[enumValue];
};

const getRamFilterOptionDisplayName = (
  enumValue: RamRangeFilterEnum
): string => {
  const displayNameMap: Record<RamRangeFilterEnum, string> = {
    [RamRangeFilterEnum.Under9Gb]: i18n.t(
      'storefront:filters.options.ram.under9Gb'
    ),
    [RamRangeFilterEnum.From9GbAndUp]: i18n.t(
      'storefront:filters.options.ram.from9GbAndUp'
    ),
    [RamRangeFilterEnum.Under16Gb]: i18n.t(
      'storefront:filters.options.ram.under16Gb'
    ),
    [RamRangeFilterEnum.Range17To35Gb]: i18n.t(
      'storefront:filters.options.ram.range17To35Gb'
    ),
    [RamRangeFilterEnum.Over36Gb]: i18n.t(
      'storefront:filters.options.ram.over36Gb'
    ),
  };
  return displayNameMap[enumValue];
};

const getSizeFilterOptionDisplayName = (
  enumValue: SizeRangeFilterEnum
): string => {
  const displayNameMap: Record<SizeRangeFilterEnum, string> = {
    [SizeRangeFilterEnum.Under12]: i18n.t(
      'storefront:filters.options.size.under12'
    ),
    [SizeRangeFilterEnum.From12AndUp]: i18n.t(
      'storefront:filters.options.size.from12AndUp'
    ),
    [SizeRangeFilterEnum.UpTo139]: i18n.t(
      'storefront:filters.options.size.upTo139'
    ),
    [SizeRangeFilterEnum.From14To159]: i18n.t(
      'storefront:filters.options.size.from14To159'
    ),
    [SizeRangeFilterEnum.Over16]: i18n.t(
      'storefront:filters.options.size.over16'
    ),
    [SizeRangeFilterEnum.Under269]: i18n.t(
      'storefront:filters.options.size.under269'
    ),
    [SizeRangeFilterEnum.Over27]: i18n.t(
      'storefront:filters.options.size.over27'
    ),
    [SizeRangeFilterEnum.Under6]: i18n.t(
      'storefront:filters.options.size.under6'
    ),
    [SizeRangeFilterEnum.From6To69]: i18n.t(
      'storefront:filters.options.size.from6To69'
    ),
    [SizeRangeFilterEnum.Over7]: i18n.t(
      'storefront:filters.options.size.over7'
    ),
  };
  return displayNameMap[enumValue];
};

const getStorageFilterOptionDisplayName = (
  enumValue: SsdRangeFilterEnum
): string => {
  const displayNameMap: Record<SsdRangeFilterEnum, string> = {
    [SsdRangeFilterEnum.UpTo255Gb]: i18n.t(
      'storefront:filters.options.storage.upTo255Gb'
    ),
    [SsdRangeFilterEnum.Range256To999Gb]: i18n.t(
      'storefront:filters.options.storage.range256To999Gb'
    ),
    [SsdRangeFilterEnum.Over1000Gb]: i18n.t(
      'storefront:filters.options.storage.over1000Gb'
    ),
  };
  return displayNameMap[enumValue];
};

const getConnectivityFilterOptionDisplayName = (
  enumValue: CellularGenerationFilterEnum
): string => {
  const displayNameMap: Record<CellularGenerationFilterEnum, string> = {
    [CellularGenerationFilterEnum.Only]: i18n.t(
      'storefront:filters.options.connectivity.wifi'
    ),
    [CellularGenerationFilterEnum.AndCellular]: i18n.t(
      'storefront:filters.options.connectivity.wifiAndCellular'
    ),
  };
  return displayNameMap[enumValue];
};

export const getFilterOptionDisplayName = (
  filterKey: DynamicFilterKey,
  filterOption: string
) => {
  switch (filterKey) {
    case DynamicFilterKey.SIZE:
      return getSizeFilterOptionDisplayName(
        filterOption as SizeRangeFilterEnum
      );
    case DynamicFilterKey.RAM:
      return getRamFilterOptionDisplayName(filterOption as RamRangeFilterEnum);
    case DynamicFilterKey.PRICE:
      return getPriceFilterOptionDisplayName(
        filterOption as PriceRangeFilterEnum
      );
    case DynamicFilterKey.STORAGE:
      return getStorageFilterOptionDisplayName(
        filterOption as SsdRangeFilterEnum
      );
    case DynamicFilterKey.CONNECTIVITY:
      return getConnectivityFilterOptionDisplayName(
        filterOption as CellularGenerationFilterEnum
      );
    case DynamicFilterKey.TYPE:
      return getProductTypeLabel(filterOption as ProductTypeName);
    case DynamicFilterKey.MANUFACTURER:
    case DynamicFilterKey.PROCESSOR:
    case DynamicFilterKey.COLOR:
    case DynamicFilterKey.CARRIER:
    case DynamicFilterKey.TELEPHONE_TYPE:
    case DynamicFilterKey.LINES_SUPPORTED:
    case DynamicFilterKey.POWER_OVER_ETHERNET:
    case DynamicFilterKey.KEYBOARD_LANGUAGE:
      return filterOption; // display the same name that came from the backend (for these filters, the backend returns the user-facing text instead of the slug)
    default:
      return formatSlugToDisplayName(filterOption);
  }
};

export const getDynamicPropertyDisplayValue = (
  slug: ProductTypePropertySlugEnum | undefined,
  value: string
): string => {
  switch (slug) {
    case ProductTypePropertySlugEnum.SsdStorage:
      if (parseFloat(value) >= 1000) {
        return `${parseFloat(value) / 1000} ${i18n.t('storefront:dynamicProperty.storageUnitTb')}`;
      }
      return `${value} ${i18n.t('storefront:dynamicProperty.storageUnitGb')}`;
    default:
      return value;
  }
};
